import styled from 'styled-components';
import { Link } from '../../../services/link';

// Constants
export const contentWidth = 1180;
export const tabletThreshold = 1180;
export const tabletThresholdPixels = `${tabletThreshold}px`;
export const mobileThreshold = 720;
export const mobileThresholdPixels = `${mobileThreshold}px`;

export const menuBarHeight = 89;
export const menuBarHeightWithBanner = 130;
export const menuBarHeightPixels = `${menuBarHeight}px`;
export const menuBarHeightWithBannerPixels = `${menuBarHeightWithBanner}px`;
export const tabletMenuBarHeight = 90;
export const tabletMenuBarHeightWithBanner = 130;
export const tabletMenuBarHeightPixels = `${tabletMenuBarHeight}px`;
export const tabletMenuBarHeightWithBannerPixels = `${tabletMenuBarHeightWithBanner}px`;
export const mobileMenuBarHeight = 52;
export const mobileMenuBarHeightWithBanner = 130;
export const mobileMenuBarHeightPixels = `${mobileMenuBarHeight}px`;
export const mobileMenuBarHeightWithBannerPixels = `${mobileMenuBarHeightWithBanner}px`;

export const colors = {
  // black and grey
  black: '#000000',
  grey800: '#1E1E1E',
  grey700: '#323232',
  grey600: '#636363',
  grey500: '#787878',
  grey400: '#A8A8A8',
  grey300: '#C4C4C4',
  grey200: '#D7D7D7',
  grey100: '#F3F2F0',
  grey50: '#F7F7F7',

  // white and beige
  white: '#FFFFFF',
  beige100: '#FEFCF9',
  beige200: '#F9EDDA',
  beige300: '#F1E6CA',
  beige500: '#EFCE9D',
  beige600: '#C2A375',
  beige700: '#9A671A',
  beige800: '#1F1505',

  // yellow
  yellow900: '#FFF500',
  yellow800: '#EEC114',
  yellow700: '#F5C946',

  // blue
  blue800: '#7F8FA4',

  // red
  red500: '#D81B46',

  // green
  green500: '#578653',

  // opacity
  opacityFilter: 'rgba(0, 0, 0, 0.7)',
  lightGrey4: 'rgba(23, 44, 66, 0.4)',
  shadow: 'rgba(223, 228, 239, 0.5)',
  whiteTransparent: 'rgba(255,255,255,0.95)',

  // old
  greenShadow: 'rgba(183, 199, 196, 0.3)',
};

export const margins = {
  m0: '4px',
  m1: '8px',
  m2: '16px',
  m3: '24px',
  m4: '32px',
  m5: '40px',
  m6: '48px',
  m7: '56px',
  m8: '64px',
  m9: '72px',
  m10: '80px',
  m11: '88px',
  m12: '96px',
  m13: '104px',
  m14: '112px',
  m15: '120px',
  m16: '128px',
  m17: '136px',
  m18: '144px',
  m19: '152px',
};

export const fontFamilies = {
  main: 'Roboto',
  storyTelling: 'Space Mono',
};

export const frames = {
  desktop: '136px',
  tablet: '64px',
  mobile: '24px',
};

export const fontSizes = {
  xs: '12px',
  s: '16px',
  m: '18px',
  l: '24px',
  xl: '30px',
  x2l: '36px',
  x3l: '46px',
  x4l: '50px',
};

type ContainerProps = {
  sticky?: boolean;
};

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${(props) => props.sticky ? '' : 'overflow-x: hidden;'};
  background-color: ${colors.beige100};
  font-size: ${fontSizes.m};
  font-family: Roboto;
`;

type ContentProps = {
  full?: boolean;
  hasNoBanner?: boolean;
  black?: boolean;
  noMarginTop?: boolean;
};

export const Content = styled.div<ContentProps>`
  font-family: Roboto;
  background-color: ${(props) => props.black ? colors.black : colors.beige100};
  ${(props) => props.full
    ? 'width: 100vw; display: flex; flex-direction: column; align-items: center;'
    : `width: ${contentWidth}px`};
  margin: auto;
  margin-top: ${({ hasNoBanner = true }) => hasNoBanner
    ? menuBarHeightPixels
    : menuBarHeightWithBannerPixels};
  margin-top: ${(props) => props.noMarginTop && 0};
  transition: margin-top 0.3s ease-in-out;

  @media (max-width: ${tabletThresholdPixels}) {
    display: block;
    margin-top: ${({ hasNoBanner = true }) => hasNoBanner
    ? mobileMenuBarHeightPixels
    : mobileMenuBarHeightWithBannerPixels};
    margin-top: ${(props) => props.noMarginTop && 0};
    width: ${(props) => props.full ? '100vw' : `calc(100vw - ${frames.mobile}*2)`};
  }
`;

type ButtonProps = {
  menu?: boolean;
  big?: boolean;
  dark?: boolean;
  border?: boolean;
  transparent?: boolean;
  transparentWithoutBorder?: boolean;
  yellow?: boolean;
  bigFontSize?: boolean;
  width?: string;
  lowercase?: boolean;
};

export const Button = styled.button<ButtonProps>`
  border-radius: 4px;
  font-size: 14px;
  min-width: ${({ width }) => width || '100px'};
  ${(props) => props.dark ? `background: ${colors.black}; color: ${colors.beige100}; border: 0px;` : `background: ${colors.beige100}; color: ${colors.black}; border: 0px;`}
  ${(props) => props.transparent && `background-color: rgba(0, 0, 0, 0); color: ${colors.black}; border: 1px solid ${colors.black};`};
  ${(props) => props.transparentWithoutBorder && `background-color: rgba(0, 0, 0, 0); color: ${colors.white}; border: none; text-decoration: underline;`};
  ${(props) => props.yellow && `background-color: ${colors.yellow900}; border: 0px;`};
  font-family: ${fontFamilies.main};
  ${(props) => props.menu ? 'padding: 12px 20px;' : 'padding: 16px;'}
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
  display: flex;
  justify-content: center;
  ${(props) => props.lowercase && 'text-transform: none;'}

  :hover {
    transform: scale(1.04);
    transition: transform 0.2s ease-in-out;
  }

  @media (max-width: ${tabletThresholdPixels}) {
    width: ${({ width }) => width || 'unset'};
    border: 1px solid ${colors.black};
    padding: 17px;
    box-sizing: border-box;
  }
`;

export const LittleButton = styled.button<ButtonProps>`
  border-radius: 4px;
  border: none;
  height: 48px;
  box-sizing: border-box;
  background: ${colors.black};
  color: ${colors.beige100};
  font-size: 12px;
  padding: 10px;
  text-transform: uppercase;
  cursor: pointer;

  @media (max-width: ${tabletThresholdPixels}) {
    width: 100%;
    min-width: 220px;
    border: 1px solid ${colors.black};
    padding: 17px;
  }
`;

export const TitleStoryTelling = styled.h3`
    font-family: ${fontFamilies.storyTelling};
    font-weight: 400;
    text-transform: uppercase;
    font-size: 60px;
    letter-spacing: 6px;
    margin: 0 0 32px;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 27px;
  }
`;

export const LineDiv = styled.div`
width: 100%;
height: 2px;
background-color: #000;
margin: 5px 0;
`;

type TitleType = {
  marginSide?: boolean;
  bigMobile?: boolean;
  white?: boolean;
};
export const Title = styled.h2<TitleType>`
  font-size: 70px;
  font-weight: 700;
  text-transform: uppercase;
  margin: 0 ${(props) => props.marginSide ? `${frames.desktop}` : 0};
  ${(props) => props.white && `color: ${colors.white}`};
  @media (max-width: ${mobileThresholdPixels}) {
    font-size: 32px;
    ${(props) => props.bigMobile ? 'font-size: 48px' : 'font-size: 32px'};
    margin: 0 ${(props) => props.marginSide ? `${frames.mobile}` : 0};
  }
`;

type SecondTitleType = {
  monospace?: boolean;
  center?: boolean;
  bold?: boolean;
};
export const SecondTitle = styled.h2<SecondTitleType>`
  margin: 0;
  font-size: 60px;
  font-family: ${(props) => props.monospace ? 'Space Mono' : 'Roboto'};
  letter-spacing: 6px;
  text-transform: uppercase;
  font-weight: ${(props) => props.bold ? 700 : 400};
  text-align: ${(props) => props.center ? 'center' : ''};
  line-height: 93%;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 40px;
  }
`;

export const LittleTitle = styled.h2`
  font-size: 32px;
  text-transform: uppercase;
  line-height: 40px;
  font-weight: 700;
  margin: 0 0 16px;
`;

export const SectionContainer = styled.div`
  position: relative;
  max-width: 1180px;
  padding: 46px 0;
  @media (max-width: ${tabletThresholdPixels}) {
    width: calc(100vw - ${frames.mobile}*2);
    padding: 0;
  }
`;

export const Text = styled.p`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  font-family: ${fontFamilies.main};
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 14px;
  }
`;

export const SectionTitle = styled.h2`
  font-family: ${fontFamilies.main};
  font-size: 96px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 9.6px;
  text-transform: uppercase;
  margin: 0;
  word-break: break-word;
  hyphens: auto;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 40px;
    letter-spacing: 4px;
    padding: 0;
  }
`;

export const TitleSeparator = styled.div`
  width: 150px;
  height: 8px;
  background-color: #000;
  @media (max-width: ${tabletThresholdPixels}) {
    display: none;
  }
`;

type UnreadMessagesIconProps = {
  little?: boolean;
};
export const UnreadMessagesIcon = styled.div<UnreadMessagesIconProps>`
  ${(props) => props.little ? 'width: 13px; height: 13px;' : 'width: 23px; height: 23px;'}
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background: ${colors.red500};
  color: ${colors.white};
  margin-left: 20px;
  font-size: 14px;
  @media (max-width: ${tabletThresholdPixels}) {
    ${(props) => props.little ? 'width: 9px; height: 9px;' : 'width: 18px; height: 18px;'}
    margin-left: 0px;
    font-size: 12px;
  }
`;

type DesktopText2Props = {
  bold?: boolean;
};
export const DesktopText2 = styled.div<DesktopText2Props>`
  font-size: 12px;
  font-weight: ${(props) => props.bold ? 700 : 400};
`;

export const OpacityContainer = styled.div`
  background: ${colors.opacityFilter};
  overflow: auto;
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  z-index: 1050;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: ${tabletThresholdPixels}) {
    height: 100vh;
    margin: 0;
    align-items: flex-start;
    padding: 0;
    overflow-y: hidden;
  }
`;

type PopupContainerProps = {
  big?: boolean;
};
export const PopupContainer = styled.div<PopupContainerProps>`
  overflow: visible;
  position: relative;
  width: ${(props) => props.big ? '500px' : '390px'};
  margin: 0 auto;
  padding: 35px 50px 35px;
  border-radius: 4px;
  background-color: ${colors.grey50};
  color: ${colors.black};
  font-size: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: ${tabletThresholdPixels}) {
    width: 90vw;
    box-sizing: border-box;
    padding: 22px 15px;
  }
`;

type ErrorMessageProps = {
  marginTop?: boolean;
};
export const ErrorMessage = styled.p<ErrorMessageProps>`
  color: ${colors.red500};
  font-size: 12px;
  ${(props) => props.marginTop && 'margin-top: -50px;'}
`;

export const Input = styled.input`
  margin: 16px 0 0;
  font-size: 20px;
  font-weight: 700;
  width: 384px;
  min-height: 48px;
  padding: 14px 8px;
  box-sizing: border-box;
  border: 1px solid ${colors.black};
  border-radius: 4px;
  ::placeholder {
    font-size: 14px;
    font-weight: 400;
    color: ${colors.grey400}
  }
  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  @media (max-width: ${mobileThresholdPixels}) {
    margin: 10px 0 20px;
    width: calc(100vw - 48px);
    font-size: 12px;
    -webkit-appearance: none;
  }
`;

type LabelProps = {
  noMarginTop?: boolean;
  htmlFor?: string;
};
export const Label = styled.p<LabelProps>`
  font-weight: 400;
  font-size: 16px;
  margin: 0;
  @media (max-width: ${tabletThresholdPixels}) {
    ${(props) => !props.noMarginTop && 'margin-top: 40px;'}
    font-size: 10px;
    text-transform: uppercase;
    text-align: center;
  }
`;

export const StyledCloseIcon = styled.img`
  cursor: pointer;
  width: 17px;
  position: absolute;
  top: 20px;
  right: 20px;
`;

export const StyledLink = styled(Link)`
  text-decoration: none;
`;
